import { Block } from '@/components/base/Block'
import InputErrorLabel from '@/components/base/Form/InputErrorLabel'
import { Col, Row } from '@/components/base/Layout'
import { Text3 } from '@/components/base/Text'
import { align, display } from '@/styles/display'
import spaceStyle from '@/styles/space'
import {
  V1FormElementStateType,
  type V1FormElementState,
  type V1FormElementUiDateRangeKubunSelectBox
} from 'mnvm-mypage-app-ts-if'
import { generateOptions } from './FormElementDateSelectBox'
import GenericFormElementSelectBox, {
  type SelectBoxStyle
} from './GenericFormElementSelectBox'

export interface FormElementDateRangeKubunProps {
  isEdit?: boolean
  uiIFM: V1FormElementUiDateRangeKubunSelectBox
  formElementState: V1FormElementState
  elemenErrorMessage?: string
  onChange: (newElementState: V1FormElementState) => void
  style?: SelectBoxStyle
}

export const FormElementDateRangeKubun = ({
  isEdit = true,
  uiIFM,
  formElementState,
  elemenErrorMessage,
  onChange,
  style
}: FormElementDateRangeKubunProps): JSX.Element => {
  // var start time
  const selectedStartYear =
    formElementState.contentStates?.at(0)?.uint32 ?? undefined
  const selectedStartMonth =
    formElementState.contentStates?.at(1)?.uint32 ?? undefined
  const selectedEntryTypes =
    formElementState.contentStates?.at(2)?.uint32 ?? undefined

  // var end time
  const selectedEndYear =
    formElementState.contentStates?.at(3)?.uint32 ?? undefined
  const selectedEndMonth =
    formElementState.contentStates?.at(4)?.uint32 ?? undefined
  const selectedGraduatedTypes =
    formElementState.contentStates?.at(5)?.uint32 ?? undefined

  const entryTypeLabel =
    uiIFM.entryTypes?.find((e) => (e.state?.uint32 ?? 0) === selectedEntryTypes)
      ?.label ?? '-'

  const graduatedTypeLabel =
    uiIFM.graduatedTypes?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedGraduatedTypes
    )?.label ?? '-'

  if (!isEdit) {
    // start date
    const startYearSelectedContent = uiIFM.years?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedStartYear
    )
    const startYearLabel =
      startYearSelectedContent != null ? startYearSelectedContent.label : '-'

    const startMonthSelectedContent = uiIFM.months?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedStartMonth
    )
    const startMonthLabel =
      startMonthSelectedContent != null ? startMonthSelectedContent.label : '-'

    // end date
    const endYearSelectedContent = uiIFM.years?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedEndYear
    )
    const endYearLabel =
      endYearSelectedContent != null ? endYearSelectedContent.label : '-'

    const endMonthSelectedContent = uiIFM.months?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedEndMonth
    )
    const endMonthLabel =
      endMonthSelectedContent != null ? endMonthSelectedContent.label : '-'

    return (
      <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
        {startYearLabel}年{startMonthLabel}月 {entryTypeLabel}～{endYearLabel}年
        {endMonthLabel}月 {graduatedTypeLabel}
      </Text3>
    )
  }

  if (formElementState.type === V1FormElementStateType.Uint32List) {
    const yearOptions = generateOptions(uiIFM.years ?? [])
    const monthOptions = generateOptions(uiIFM.months ?? [])
    const entryTypeOptions = generateOptions(uiIFM.entryTypes ?? [])
    const graduatedTypeOptions = generateOptions(uiIFM.graduatedTypes ?? [])

    return (
      <>
        <Row>
          <Col span={11} xs={24}>
            <Block
              display={display.FLEX}
              alignItems={align.CENTER}
              style={{ gap: '16px' }}
            >
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_start_year"
                  options={yearOptions}
                  selectedValue={selectedStartYear}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: entryTypeLabel,
                          uint32: selectedEntryTypes
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: selectedGraduatedTypes
                        }
                      ]
                    })
                  }}
                  label={{ right: '年' }}
                  style={style}
                />
              </Block>
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_start_month"
                  options={monthOptions}
                  selectedValue={selectedStartMonth}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: entryTypeLabel,
                          uint32: selectedEntryTypes
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: selectedGraduatedTypes
                        }
                      ]
                    })
                  }}
                  label={{ right: '月' }}
                  style={style}
                />
              </Block>
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_entry_types"
                  options={entryTypeOptions}
                  selectedValue={selectedEntryTypes}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: entryTypeLabel,
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: selectedGraduatedTypes
                        }
                      ]
                    })
                  }}
                  style={style}
                />
              </Block>
            </Block>
          </Col>
          <Col span={2} justify={align.CENTER} align={align.CENTER}>
            <span>～</span>
          </Col>
          <Col span={11} xs={24}>
            <Block
              display={display.FLEX}
              alignItems={align.CENTER}
              style={{ gap: '16px' }}
            >
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_end_year"
                  options={yearOptions}
                  selectedValue={selectedEndYear}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: entryTypeLabel,
                          uint32: selectedEntryTypes
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: selectedGraduatedTypes
                        }
                      ]
                    })
                  }}
                  label={{ right: '年' }}
                  style={style}
                />
              </Block>
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_month"
                  options={monthOptions}
                  selectedValue={selectedEndMonth}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: entryTypeLabel,
                          uint32: selectedEntryTypes
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: selectedGraduatedTypes
                        }
                      ]
                    })
                  }}
                  label={{ right: '月' }}
                  style={style}
                />
              </Block>
              <Block width="33%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_graduated_types"
                  options={graduatedTypeOptions}
                  selectedValue={selectedGraduatedTypes}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: entryTypeLabel,
                          uint32: selectedEntryTypes
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        },
                        {
                          label: graduatedTypeLabel,
                          uint32: Number(selectedValue)
                        }
                      ]
                    })
                  }}
                  style={style}
                />
              </Block>
            </Block>
          </Col>
        </Row>
        {elemenErrorMessage !== undefined && (
          <InputErrorLabel message={elemenErrorMessage} />
        )}
      </>
    )
  }

  return (
    <>
      <div>not implemented</div>
    </>
  )
}
